/**
 * PR-LT3 variant A (A2) — FIX (Codex P2). Build a bucket's per-SEGMENT
 * `instanceLtScale` reciprocal stream, folding the FULL effective linetype
 * scale per segment = `globalScale(thisSegment's space) × CELTSCALE(thisEntity)`.
 *
 * # Why the fold is per-segment (mixed-space buckets + relift)
 *
 * Before this fix the GLOBAL factor (`LTSCALE × MSLTSCALE|PSLTSCALE`) rode the
 * material `dashScale` uniform, chosen ONCE per bucket by the bucket's space
 * class. That is wrong for a MIXED bucket — a model line and a paper line that
 * happen to share one `layer × color × linetype` MeshKey (space is NOT part of
 * the key) — because the whole bucket got the model global scale, so paper
 * segments rendered the wrong dash period when PSLTSCALE differed (e.g.
 * PSLTSCALE = 0). And the per-bucket uniform never reached the relift path's
 * incremental re-upload.
 *
 * The fix folds the global scale into the per-SEGMENT `instanceLtScale`
 * attribute, by the segment's OWN space:
 *   - `instanceLtScale[s] = 1 / (globalScale(space[s]) × CELTSCALE(entity[s]))`
 *     stored as the reciprocal (consistent with the existing convention — the
 *     vertex shader multiplies `vLineDistance` by it).
 *   - The material `dashScale` uniform becomes a constant `1` (the global scale
 *     is now per-segment).
 *
 * `computeGlobalLtScale` (the single source of the scale math, shared with the
 * PDF exporter via `computeEffectiveDash`) is evaluated per-space here, and
 * memoised (only model + paper are possible) so a large bucket pays at most two
 * `computeGlobalLtScale` calls regardless of segment count.
 *
 * # Per-vertex → per-segment collapse
 *
 * The bucket's `instanceLtScales` / `instanceSpaceIsPaper` are PER-VERTEX
 * streams (2 endpoints per segment, both carrying the same per-entity value).
 * `LineSegments2` draws one INSTANCE per segment, so the instanced attribute
 * needs exactly ONE value per segment — sample the START endpoint of each
 * segment (every other vertex, index `2*s`). This matches
 * `instanceStart.count` = segments = `vertexLength / 6`.
 */

import type { MeshBucket } from "../batchedLayer";
import {
  computeGlobalLtScale,
  type LinetypeSysvars,
} from "./linetypePatterns";

/** Default sysvars (all-1) — yields global scale 1 (the pre-A2 render). */
const DEFAULT_LT_SYSVARS: LinetypeSysvars = {
  LTSCALE: 1,
  MSLTSCALE: 1,
  PSLTSCALE: 1,
};

/**
 * Build the per-SEGMENT `instanceLtScale` reciprocal array for a bucket,
 * folding the global MS/PS LTSCALE (selected per-segment by the segment's own
 * model/paper space) into the per-entity CELTSCALE reciprocal already stored in
 * `bucket.instanceLtScales`.
 *
 * @param bucket   The BatchedLayer bucket (per-vertex `instanceLtScales` =
 *                 `1 / CELTSCALE`, `instanceSpaceIsPaper` = per-vertex space flag).
 * @param sysvars  Document LTSCALE / MSLTSCALE / PSLTSCALE. Absent → all-1 →
 *                 global scale 1 (the per-segment value equals `1 / CELTSCALE`,
 *                 the pre-fold behaviour).
 * @returns A `Float32Array` of length `vertexLength / 6` (one per segment),
 *          each = `1 / (globalScale(space) × CELTSCALE)`.
 */
export function buildPerSegmentLtScales(
  bucket: Pick<
    MeshBucket,
    | "vertexLength"
    | "instanceLtScales"
    | "instanceLtScaleLength"
    | "instanceSpaceIsPaper"
    | "instanceSpaceIsPaperLength"
  >,
  sysvars?: LinetypeSysvars,
): Float32Array {
  const sv = sysvars ?? DEFAULT_LT_SYSVARS;
  // Memoise the two possible global factors. `computeGlobalLtScale` already
  // returns `1` for a degenerate / non-positive result, so each is > 0.
  const globalModel = computeGlobalLtScale(sv, "model");
  const globalPaper = computeGlobalLtScale(sv, "paper");
  // Pre-compute reciprocals; multiply into the per-entity reciprocal so the
  // result is `1 / (global × CELTSCALE)`.
  const recipModel = globalModel > 0 ? 1 / globalModel : 1;
  const recipPaper = globalPaper > 0 ? 1 / globalPaper : 1;

  const vertexLtScales = bucket.instanceLtScales.subarray(
    0,
    bucket.instanceLtScaleLength,
  );
  const spaceIsPaper = bucket.instanceSpaceIsPaper.subarray(
    0,
    bucket.instanceSpaceIsPaperLength,
  );
  const segmentCount = bucket.vertexLength / 6;
  const out = new Float32Array(segmentCount);
  for (let s = 0; s < segmentCount; s++) {
    // Per-vertex stream: 2 entries per segment (start, end). Both hold the same
    // entity value — take the start (index 2*s).
    const celtRecip = vertexLtScales[s * 2] ?? 1;
    const globalRecip = (spaceIsPaper[s * 2] ?? 0) >= 0.5 ? recipPaper : recipModel;
    out[s] = celtRecip * globalRecip;
  }
  return out;
}

/**
 * fix/selection-highlight-not-applied — build a bucket's per-SEGMENT
 * `instanceSlotIndex` stream (the texel index the state-aware shader samples to
 * find an entity's selection / hover / preview byte).
 *
 * # Why this exists
 *
 * `bucket.instanceSlotIndices` is a PER-VERTEX stream (2 entries per segment,
 * both carrying the same slot) and used to be bound with a plain
 * `THREE.Float32BufferAttribute`. But `LineSegments2` draws ONE INSTANCE per
 * segment from a fixed quad prototype, so a NON-instanced attribute is indexed
 * by the PROTOTYPE's vertex id, not by the segment: every quad reads
 * `instanceSlotIndices[0..3]` no matter which segment it is drawing. Two of a
 * segment's four corners therefore sample the WRONG slot — the next segment's,
 * or, on the last/only segment of a bucket, past the end of the populated range
 * (0 by WebGL's out-of-range attribute rule, i.e. "not selected").
 *
 * The visible result is a selected entity whose body is only PARTLY repainted.
 * Measured with `gl.readPixels` inside the frame for one selected LINE:
 * `lit 2751 / cyan 412 / white 1494`, and the split moves with the camera
 * because `vSelectionState` is interpolated across the quad — which is the
 * per-run variability recorded in `MBCR-selection-highlight-intermittent`.
 *
 * Collapsing to one value per segment and binding it as a TRUE
 * `InstancedBufferAttribute` (divisor 1, aligned 1:1 with
 * `instanceStart`/`instanceEnd`) makes the lookup per-entity — exactly what
 * `setLinetypeScales` already does for `instanceLtScale`.
 *
 * @returns A `Float32Array` of length `vertexLength / 6` (one slot per segment).
 */
export function buildPerSegmentSlotIndices(
  bucket: Pick<
    MeshBucket,
    "vertexLength" | "instanceSlotIndices" | "instanceSlotLength"
  >,
): Float32Array {
  const slots = bucket.instanceSlotIndices.subarray(
    0,
    bucket.instanceSlotLength,
  );
  const segmentCount = Math.floor(bucket.vertexLength / 6);
  const out = new Float32Array(segmentCount);
  for (let s = 0; s < segmentCount; s++) {
    // Same start-endpoint sampling as `buildPerSegmentLtScales`: both vertices
    // of a segment carry the entity's slot, so index `2*s` is authoritative.
    out[s] = slots[s * 2] ?? 0;
  }
  return out;
}
